import React, { useState, useEffect } from 'react';
import { Status, DEFAULT_PIVOT_IDS_OBJ } from 'common/utils/constants';

import { TwoHopPivotSelection } from './TwoHopPivotSelection/TwoHopPivotSelection';
import { TwoHopPivotDetails } from './TwoHopPivotDetails/TwoHopPivotDetails';
import TwoHopSettings from './TwoHopSettings/TwoHopSettings';
import TwoHopAdvancedSettings from './TwoHopAdvancedSettings/TwoHopAdvancedSettings';

import { getTwoHopSettings } from 'apis/commissioningApis/getTwoHopsSettings';
import TwoHopPodCreation from './TwoHopPodCreation/TwoHopPodCreation';
import { doCreateBundle } from 'redux/actions';
import { useDispatch } from 'react-redux';
import { startTwoHopAlignment } from 'apis/commissioningApis/StartTwoHopAlignment';
import { TWO_HOP_ALIGNMENT_SCREEN_INDEXES } from './utils';

/**
 * This paraent component manages dither setting and advanced setting data
 */
const TwoHopAlignment = (props) => {
  const [screenIndex, setScreenIndex] = useState(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.PIVOT_SELECTION);
  const [pivotIds, setPivotIds] = useState(DEFAULT_PIVOT_IDS_OBJ);
  const [pivotData, setPivotData] = useState();
  const [alignmentStarted, setAlignmentStarted] = useState(false);
  const [selectedDevicesOne, setSelectedDevicesOne] = useState([{}, {}, {}]);
  const [selectedDevicesTwo, setSelectedDevicesTwo] = useState([{}, {}, {}]);
  const [createBundleErrorCode, setCreateBundleErrorCode] = useState(null);
  const [newBundleIdOne, setNewBundleIdOne] = useState(null);
  const [newBundleIdTwo, setNewBundleIdTwo] = useState(null);
  const [isHopOneDevicesValid, setIsHopOneDevicesValid] = useState(null);
  const [isHopTwoDevicesValid, setIsHopTwoDevicesValid] = useState(null);
  const [nonExistHopOneDevices, setNoneExistHopOneDevices] = useState(null);
  const [nonExistHopTwoDevices, setNoneExistHopTwoDevices] = useState(null);

  const [ditherSettings, setDitherSettings] = useState();
  const [ditherRequestStatus, setDitherRequestStatus] = useState(Status.NONE);
  const [advancedSettings, setAdvancedSettings] = useState();
  const [advancedRequestStatus, setAdvancedRequestStatus] = useState(Status.NONE);

  const dispatch = useDispatch();

  useEffect(() => {
    setDitherRequestStatus(Status.PENDING);
    getTwoHopSettings()
      .then((res) => {
        setDitherSettings(res);
        setDitherRequestStatus(Status.SUCCESS);
      })
      .catch((e) => {
        setDitherRequestStatus(Status.FAILURE);
      });
    getTwoHopSettings(true)
      .then((res) => {
        setAdvancedSettings(res);
        setAdvancedRequestStatus(Status.SUCCESS);
      })
      .catch((e) => {
        setAdvancedRequestStatus(Status.FAILURE);
      });
  }, []);

  const ditherSettingOnSave = (newSettings) => setDitherSettings({ ...ditherSettings, settings: newSettings });

  const advancedSettingOnSave = (newSettings, index) => {
    let advancedSettingsCopy = { ...advancedSettings };
    advancedSettingsCopy.tables[index].settings = newSettings;

    setAdvancedSettings(advancedSettingsCopy);
  };

  const startAlignmentOnCLick = () => {
    if (alignmentStarted) return;
    if ([ditherRequestStatus, advancedRequestStatus].some((status) => status !== Status.SUCCESS)) return;

    let settings = getCurrentSettingValues(ditherSettings.settings);
    let pivot1DU = getCurrentSettingValues(advancedSettings.tables[0].settings);
    let pivot1SU1 = getCurrentSettingValues(advancedSettings.tables[1].settings);

    const requestBody = {
      target: {
        pivot1DU: pivotIds.hop1donor1,
        pivot1SU1: pivotIds.hop1service1,
        pivot1SU2: pivotIds.hop1service2,
        pivot2DU: pivotIds.hop2donor1,
        pivot2SU1: pivotIds.hop2service1,
        pivot2SU2: pivotIds.hop2service2
      },
      settings,
      advancedSettings: { pivot1DU, pivot1SU1 }
    };
    setAlignmentStarted(true);
    startTwoHopAlignment(requestBody)
      .then((res) => {
        props.onClose();
        props.actionsPageThis.setState({ refreshActionList: true });
      })
      .catch((e) => {
        console.error('2 hop alignment did not start ', e);
      });
  };

  const getCurrentSettingValues = (settings) => {
    let newValues = {};

    settings.forEach((parameter) => {
      const name = parameter.name;
      const value = parameter.current ?? parameter.default;

      newValues[name] = value;
    });

    return newValues;
  };

  const createBundle = (bundleObj, pod) => {
    let cb_success = (result) => {
      let bundleId = result?.id;
      if (pod === 'podOne') setNewBundleIdOne(bundleId);
      if (pod === 'podTwo') setNewBundleIdTwo(bundleId);
    };

    let cb_failed = (error) => {
      setCreateBundleErrorCode(error.message);
    };

    bundleObj && dispatch(doCreateBundle(bundleObj, cb_success, cb_failed));
  };

  const hasBundledDevices = (selectedDevices = []) => {
    let hasBundle = false;
    let empty = selectedDevices.every((obj) => Object.keys(obj).length === 0);
    if (empty) {
      hasBundle = true;
      return hasBundle;
    }
    for (let device of selectedDevices) {
      if (device?.isBundled) {
        hasBundle = true;
        break;
      }
    }
    return hasBundle;
  };

  const onNext = async (index, data) => {
    if (index === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.PIVOT_SELECTION) {
      setScreenIndex(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.PIVOT_DETAILS);
    } else if (index === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.PIVOT_DETAILS) {
      if (hasBundledDevices(selectedDevicesOne) && hasBundledDevices(selectedDevicesTwo)) {
        setScreenIndex(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.SETTINGS);
      } else if (!hasBundledDevices(selectedDevicesOne)) {
        setScreenIndex(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.POD_CREATION_ONE);
      } else if (!hasBundledDevices(selectedDevicesTwo)) {
        setScreenIndex(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.POD_CREATION_TWO);
      }
    } else if (index === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.POD_CREATION_ONE) {
      createBundle(data, 'podOne');
      if (!hasBundledDevices(selectedDevicesTwo)) {
        setScreenIndex(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.POD_CREATION_TWO);
      } else {
        setScreenIndex(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.SETTINGS);
      }
    } else if (index === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.POD_CREATION_TWO) {
      createBundle(data, 'podTwo');
      setScreenIndex(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.SETTINGS);
    } else if (index === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.SETTINGS) {
      setScreenIndex(screenIndex + 1);
    } else if (index === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.ADVANCED_SETTINGS) {
      props.onClose();
    }
  };

  return (
    <div>
      {screenIndex === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.PIVOT_SELECTION && (
        <TwoHopPivotSelection
          onNext={onNext}
          onClose={props.onClose}
          sendPivots={(pivotIds) => setPivotIds(pivotIds)}
          sendTwinDict={(twinDict) => setPivotData(twinDict)}
          pivotIds={pivotIds}
          setSelectedDevicesOne={setSelectedDevicesOne}
          setSelectedDevicesTwo={setSelectedDevicesTwo}
          setNewBundleIdOne={setNewBundleIdOne}
          setNewBundleIdTwo={setNewBundleIdTwo}
          isHopOneDevicesValid={isHopOneDevicesValid}
          isHopTwoDevicesValid={isHopTwoDevicesValid}
          setIsHopOneDevicesValid={setIsHopOneDevicesValid}
          setIsHopTwoDevicesValid={setIsHopTwoDevicesValid}
          nonExistHopOneDevices={nonExistHopOneDevices}
          nonExistHopTwoDevices={nonExistHopTwoDevices}
          setNoneExistHopOneDevices={setNoneExistHopOneDevices}
          setNoneExistHopTwoDevices={setNoneExistHopTwoDevices}
        />
      )}
      {screenIndex === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.PIVOT_DETAILS && (
        <TwoHopPivotDetails onNext={onNext} onClose={props.onClose} mapperDict={pivotIds} twinDict={pivotData} />
      )}
      {screenIndex === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.POD_CREATION_ONE && (
        <TwoHopPodCreation
          podNumber={1}
          onNext={onNext}
          onClose={props.onClose}
          mapperDict={pivotIds}
          twinDict={pivotData}
          selectedDevices={selectedDevicesOne}
        />
      )}
      {screenIndex === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.POD_CREATION_TWO && (
        <TwoHopPodCreation
          podNumber={2}
          onNext={onNext}
          onClose={props.onClose}
          mapperDict={pivotIds}
          twinDict={pivotData}
          selectedDevices={selectedDevicesTwo}
        />
      )}
      {screenIndex === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.SETTINGS && (
        <TwoHopSettings
          onNext={onNext}
          onClose={props.onClose}
          requestStatus={ditherRequestStatus}
          settingsData={ditherSettings}
          advancedSettingsOnClick={() => setScreenIndex(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.ADVANCED_SETTINGS)}
          onSave={ditherSettingOnSave}
          startAlignmentOnCLick={startAlignmentOnCLick}
        />
      )}
      {screenIndex === TWO_HOP_ALIGNMENT_SCREEN_INDEXES.ADVANCED_SETTINGS && (
        <TwoHopAdvancedSettings
          onClose={props.onClose}
          requestStatus={advancedRequestStatus}
          advancedSettingsData={advancedSettings}
          backOnClick={() => setScreenIndex(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.SETTINGS)}
          onSave={advancedSettingOnSave}
        />
      )}
    </div>
  );
};

export default TwoHopAlignment;
